import React from 'react';
import { useTranslation } from 'next-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import * as Notifications from '@common/Notification';
import { AddressSearch } from '@components/address';
import { ERROR_CODES } from '@constants/errors';
import { MUTATION_KEYS, QUERY_KEYS } from '@constants/query-keys';
import { useDeliveryInfoDispatchHelpers } from '@context/DeliveryInfoContext';
import { useModalDispatchHelpers } from '@context/ModalContext';
import { getPartialAddress } from '@helpers/AddressHelpers';
import { MODAL_TYPES } from '@helpers/LayoutHelpers';
import * as OrderHelpers from '@helpers/OrderHelpers';
import { useAnalytics } from '@hooks/useAnalytics';
import * as OrdersService from '@services/OrdersService';

export const useOrderProductMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useOrderProductMutationInternal();

  const { showModal } = useModalDispatchHelpers();
  const { showDeliveryInfoModal } = useDeliveryInfoDispatchHelpers();

  const showAddressSearchModal = React.useCallback(() => showDeliveryInfoModal(<AddressSearch />), [showDeliveryInfoModal]);

  const handleAdd = async ({ type, payload }) => {
    const {
      bypassAddressRequirement,
      bypassQuantityVariants,
      order,
      listProps,
      onError = () => {},
      product,
      onSettled = () => {}
    } = payload;

    const partialAddress = getPartialAddress();

    // Product quantity variants modal

    const handleMultipleQuantityVariants = ({ order: currentOrder } = {}) => {
      onSettled();
      showModal(MODAL_TYPES.PRODUCT_VARIANTS, { product, listProps, order: currentOrder });
    };

    // Address required modal

    const handleAddressRequired = () => {
      onSettled();
      showAddressSearchModal();
    };

    const shouldShowAddressSearchModal = !bypassAddressRequirement && !partialAddress;
    const shouldShowQuantityVariantsModal = !bypassQuantityVariants && product?.canAddMultiple;

    // Unauthenticated & not created order
    if (order.isNotCreated) {
      try {

        // Create new order
        const newOrder = await OrdersService.createOrder();
        queryClient.setQueryData([QUERY_KEYS.ACTIVE_ORDER], newOrder);

        if (shouldShowQuantityVariantsModal) {
          return handleMultipleQuantityVariants({ order: newOrder });
        }

        if (shouldShowAddressSearchModal) {
          handleAddressRequired();
        }

        return mutation.mutate({ payload: { ...payload, order: newOrder }, type });

      } catch (err) {
        console.error('Error while creating a new order', err);
        onError();
        onSettled();
      }

    // Authenticated order
    } else {

      if (shouldShowQuantityVariantsModal) {
        return handleMultipleQuantityVariants({ order });
      }

      if (shouldShowAddressSearchModal && !OrderHelpers.hasAddressSet(order)) {
        handleAddressRequired();
      }
      return mutation.mutate({ payload, type });
    }

  };

  return {
    isLoading: mutation.isPending,
    isError: mutation.isError,
    //
    add: async (payload = {}) => {
      await handleAdd({ type: ACTIONS.ADD, payload });
    },
    bulkAdd: async (payload = {}) => {
      await handleAdd({ type: ACTIONS.BULK_ADD, payload });
    },
    remove: (payload) => mutation.mutate({ payload, type: ACTIONS.REMOVE }),
    edit: (payload) => mutation.mutate({ payload, type: ACTIONS.EDIT }),
    setComment: (payload) => mutation.mutate({ payload, type: ACTIONS.SET_COMMENT }),
  };
};

const useOrderProductMutationInternal = () => {

  const { t } = useTranslation();
  const analytics = useAnalytics();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [MUTATION_KEYS.ORDER_PRODUCT],
    mutationFn: (args = {}) => {
      const { type, payload = {} } = args;
      switch (type) {

        case ACTIONS.ADD: {
          const {
            order, product, replaceProductVariantCode, quantity
          } = payload;
          return OrdersService.addProduct(order, product, replaceProductVariantCode, quantity);
        }

        case ACTIONS.BULK_ADD: {
          const {
            order, products
          } = payload;
          return OrdersService.bulkAddProducts(order, products);
        }

        case ACTIONS.REMOVE: {
          const { order, orderItem } = payload;
          return OrdersService.removeProduct(order, orderItem?.id);
        }
        case ACTIONS.EDIT: {
          const { order, orderItem, quantity } = payload;
          return OrdersService.editProduct(order, orderItem?.id, quantity);
        }

        case ACTIONS.SET_COMMENT: {
          const { order, orderItem, comment } = payload;
          return OrdersService.setProductComment(order, orderItem?.id, comment);
        }

        default:
          console.error('Unknown mutation type', type);
      }
    },
    retry: (failureCount, error) => (
      error?.response?.status === ERROR_CODES.SERVER_ERROR
      && failureCount < 1
    ),
    onMutate: async (args = {}) => {
      const { payload = {}, type } = args;
      const { orderItem = {}, quantity, comment } = payload;
      const { id: orderItemId } = orderItem;

      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.ACTIVE_ORDER] });
      const previousState = queryClient.getQueryData([QUERY_KEYS.ACTIVE_ORDER]);

      switch (type) {

        case ACTIONS.ADD: {
          const { product = {}, replaceProductVariantCode } = payload;

          queryClient.setQueryData([QUERY_KEYS.ACTIVE_ORDER], (order) => ({
            ...order,
            items: [
              createNewItem(product, quantity),
              ...(
                replaceProductVariantCode
                  ? order?.items.filter((item) => item.variantCode !== replaceProductVariantCode)
                  : order?.items
              ) || []
            ]
          }));
          break;
        }

        case ACTIONS.BULK_ADD: {
          const { products = [] } = payload;

          queryClient.setQueryData([QUERY_KEYS.ACTIVE_ORDER], (order) => {
            const { items = [] } = order || {};

            const itemsMap = items.reduce((acc, item) => {
              const { productSlug } = item;
              acc[productSlug] = item;
              return acc;
            }, {});

            const newItems = products.map((p) => {
              const { product } = p || {};
              const { slug } = product || {};
              if (itemsMap[slug]) return null;
              return createNewItem(product);
            }).filter((i) => !!i);

            return {
              ...order,
              items: [
                ...newItems,
                ...items
              ]
            };
          });

          break;
        }

        case ACTIONS.EDIT: {
          queryClient.setQueryData([QUERY_KEYS.ACTIVE_ORDER], (order) => ({
            ...order,
            items: order.items.map((item) => (
              item.id === orderItemId
                ? { ...item, quantity, isOk: true }
                : item
            ))
          }));
          break;
        }

        case ACTIONS.REMOVE: {
          queryClient.setQueryData([QUERY_KEYS.ACTIVE_ORDER], (order) => ({
            ...order,
            items: order?.items?.filter((item) => item.id !== orderItemId)
          }));
          break;
        }

        case ACTIONS.SET_COMMENT: {
          queryClient.setQueryData([QUERY_KEYS.ACTIVE_ORDER], (order) => ({
            ...order,
            items: order.items.map((item) => (
              item.id === orderItemId
                ? { ...item, comment }
                : item
            ))
          }));
          break;
        }

        default:
          console.error('Unknown mutation type (on mutate)', type);
      }

      if (payload.onMutate) {
        payload.onMutate();
      }

      return { previousState };
    },
    onSuccess: (data, args = {}) => {
      const { payload = {}, type } = args;

      switch (type) {

        case ACTIONS.ADD: {
          const {
            product = {}, listProps = {}, quantity = 1
          } = payload;

          const orderItem = data?.items?.find((item) => item?.productSlug === product.slug);
          const orderItemId = orderItem?.id;

          queryClient.removeQueries({ queryKey: [QUERY_KEYS.ADD_TO_ORDER] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDER_LOYALTY] });
          queryClient.setQueryData([QUERY_KEYS.ACTIVE_ORDER], (order) => (
            {
              ...order,
              items: order?.items.map((item) => (
                item.productSlug === product.slug
                  ? {
                    ...item, quantity, id: orderItemId, isLoading: false
                  }
                  : item
              ))
            }));

          analytics.addToCart(product, { id: listProps?.analyticsListId, name: listProps?.analyticsListName }, quantity);
          break;
        }

        case ACTIONS.BULK_ADD: {
          const {
            products = []
          } = payload;

          const productsMap = products.reduce((acc, p) => {
            const { quantity: productQuantity, product } = p || {};
            const { slug } = product || {};
            acc[slug] = {
              quantity: productQuantity,
              product,
            };
            return acc;
          }, {});

          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDER_LOYALTY] });
          queryClient.setQueryData([QUERY_KEYS.ACTIVE_ORDER], (order) => ({
            ...order,
            items: order?.items.map((item) => {
              const { productSlug } = item;
              const product = productsMap[productSlug];

              if (product) {
                return {
                  ...item,
                  quantity: item.quantity + product.quantity,
                };
              }
              return item;
            })
          }));

          break;
        }

        case ACTIONS.EDIT: {
          const {
            product = {}, listProps = {}, orderItem = {}, quantity, previousQuantity
          } = payload;
          const { id } = orderItem;
          const orderItemId = data?.items?.find((item) => item.id === id);

          queryClient.removeQueries({ queryKey: [QUERY_KEYS.ADD_TO_ORDER] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDER_LOYALTY] });
          queryClient.setQueryData([QUERY_KEYS.ACTIVE_ORDER], (order) => (
            {
              ...order,
              items: order?.items.map((item) => (item.id === orderItemId ? { ...item, subtotal: orderItem?.subtotal } : item))
            }));

          if (previousQuantity < quantity) {
            analytics.addToCart(product, { id: listProps?.analyticsListId, name: listProps?.analyticsListName }, quantity - previousQuantity);
          } else {
            analytics.removeFromCart(product, { id: listProps?.analyticsListId, name: listProps?.analyticsListName }, previousQuantity - quantity);
          }

          break;
        }

        case ACTIONS.REMOVE: {
          const {
            product = {}, listProps = {}, orderItem = {}
          } = payload;

          const { quantity } = orderItem;

          queryClient.removeQueries({ queryKey: [QUERY_KEYS.ADD_TO_ORDER] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORDER_LOYALTY] });
          analytics.removeFromCart(product, { id: listProps?.analyticsListId, name: listProps?.analyticsListName }, quantity);

          break;
        }

        case ACTIONS.SET_COMMENT:
          break;

        default:
          console.error('Unknown mutation type (on success)', type);
      }

      if (payload.onSuccess) {
        payload.onSuccess(data);
      }
    },
    onError: (err = {}, args = {}, context) => {
      const { response = {} } = err;
      const { payload = {}, type } = args;
      const { previousState } = context;

      const { status, data } = response;

      const errorToastId = ERROR_TYPES[type];
      const errorMessage = status === ERROR_CODES.VALIDATION_ERROR
        ? data?.violations?.[0]?.message || t('ERRORS.DEFAULT_TOAST')
        : t('ERRORS.DEFAULT_TOAST');

      switch (type) {

        case ACTIONS.ADD: {
          analytics.viewCartError(errorMessage);
          break;
        }

        case ACTIONS.BULK_ADD: {
        // TODO: add
          break;
        }

        case ACTIONS.EDIT: {
          const { orderItem = {} } = payload;
          const { id: orderItemId } = orderItem;
          const previousItemState = previousState?.items?.find((item) => item.id === orderItemId);

          queryClient.setQueryData([QUERY_KEYS.ACTIVE_ORDER], (order) => (
            {
              ...order,
              items: order?.items.map((item) => (item.id === orderItemId ? previousItemState : item))
            }));

          analytics.viewCartError(errorMessage);
          break;
        }

        case ACTIONS.REMOVE: {
          analytics.viewCartError(errorMessage);
          break;
        }

        case ACTIONS.SET_COMMENT: {
          const { orderItem = {} } = payload;
          const { id: orderItemId } = orderItem;
          const previousItemState = previousState?.items?.find((item) => item.id === orderItemId);

          queryClient.setQueryData([QUERY_KEYS.ACTIVE_ORDER], (order) => (
            {
              ...order,
              items: order?.items.map((item) => (item.id === orderItemId ? previousItemState : item))
            }));
          break;
        }

        default:
          console.error('Unknown mutation type (on error)', type);
      }

      Notifications.showError(errorMessage, { autoClose: 2000, toastId: errorToastId });

      if (payload.onError) {
        payload.onError(err);
      }
    },
    onSettled: (data, error, args = {}) => {
      const { payload = {} } = args;

      if (payload.onSettled) {
        payload.onSettled(data, error);
      }
    }
  });
};

// Helpers

const createNewItem = (product, quantity) => {
  const {
    image, maxAllowedQuantity, maxAvailableQuantity,
    name, slug, variantCode, breadcrumbs,
    // in case an orderItem is passed instead of a regular product
    productName, productSlug
  } = product;

  return {
    quantity: quantity || 0,
    image,
    productName: name || productName,
    productSlug: slug || productSlug,
    variantCode,
    maxAllowedQuantity,
    maxAvailableQuantity,
    isLoading: true,
    isOk: true,
    breadcrumbs
  };
};

// Constants

const ACTIONS = {
  ADD: 'ADD',
  BULK_ADD: 'BULK_ADD',
  EDIT: 'EDIT',
  REMOVE: 'REMOVE',
  SET_COMMENT: 'SET_COMMENT',
};

const ERROR_TYPES = {
  ADD: 'productAddError',
  BULK_ADD: 'productBulkAddError',
  EDIT: 'productEditError',
  REMOVE: 'productRemoveError',
  SET_COMMENT: 'setCommentError',
};
